<template>
  <article class="powerball-xs-and-os-play-slip-item pb-300">
    <ul class="flex flex-col items-center px-100 pb-100">
      <li
        v-for="({ name: fieldName, selectableMax, selectableNumbers }, fieldIndex) in fieldConfig"
        :key="fieldName"
        ref="fields"
        class="play w-full"
      >
        <LcCard header-class="px-100 pt-150" body-class="px-100 py-150">
          <template #header>
            <LcLotteryRemainingPicks
              :complete="isPlayComplete"
              v-slot="{ countClass }"
              :done-label="$t('powerballxsandos.remainingnumbers.done')"
            >
              <i18n-t
                keypath="powerballxsandos.remainingnumbers"
                scope="global"
                :plural="getVisualRemaining(fieldIndex)"
              >
                <template #remaining>
                  <span :class="countClass">{{ getVisualRemaining(fieldIndex) }}</span>
                </template>
              </i18n-t>
            </LcLotteryRemainingPicks>
          </template>

          <LcLotteryNumberSelector
            class="selector justify-center"
            v-bind="{ disabled, selectableMax, selectableNumbers }"
            :label="$t('powerballxsandos.playslip.selector.legend')"
            variant="custom-cell"
            :value="play.fields[fieldIndex].selected"
            @complete="scrollToNextIncompleteField"
            @update:value="(numbers) => setSelectedNumbersForField(play.id, fieldIndex, numbers)"
          >
            <template #cell="{ checked, number }">
              <LcLotteryTeamTile
                v-bind="{ lotteryId, licensedTerritory, logoVariant }"
                :team="decodeXsAndOsTeamName(number)"
                :selected="checked"
              />
            </template>
          </LcLotteryNumberSelector>
        </LcCard>
      </li>
    </ul>
  </article>
</template>

<script lang="ts" setup>
import { decodeXsAndOsTeamName, LcLotteryTeamTile } from '@lottocom/frontend-lotteries'

import type { PlaySlipPlay } from '#product/app/composables/playSlip'

type PowerballXsAndOsBasePlaySlipItemEmits = {
  (e: 'play-state-changed', isDirty: boolean, playId: string): void
}

type PowerballXsAndOsBasePlaySlipItemProps = {
  disabled?: boolean
  play: PlaySlipPlay
}

const { play } = defineProps<PowerballXsAndOsBasePlaySlipItemProps>()
const emit = defineEmits<PowerballXsAndOsBasePlaySlipItemEmits>()

const fieldRefs = useTemplateRef<HTMLElement[]>('fields')

const { fieldConfig, licensedTerritory, lotteryId, setSelectedNumbersForField } = usePlaySlip()
const { scrollTop } = useScrollUtils()

const logoVariant = useXsAndOsLogoVariant()

const isPlayComplete = computed(() => play.isComplete)

const getVisualRemaining = (fieldIndex: number) => {
  const remaining =
    fieldConfig.value[fieldIndex].selectableMax - play.fields[fieldIndex].selected.length
  return remaining > 1 ? remaining : 1
}

const scrollToNextIncompleteField = () => {
  const nextIncompleteFieldIndex = play.fields.findIndex(
    ({ selected }, index) => selected.length < fieldConfig.value[index].selectableMax,
  )
  if (nextIncompleteFieldIndex < 0) return

  const nextFieldRef = fieldRefs.value?.[nextIncompleteFieldIndex]
  if (!nextFieldRef) return

  scrollTop(nextFieldRef.offsetTop)
}

watch(isPlayComplete, () => {
  emit('play-state-changed', isPlayComplete.value, play.id)
})
</script>

<style lang="scss" scoped>
.powerball-xs-and-os-play-slip-item {
  $tile-width: token('size/850');
  $tile-gap: 0.5rem;
  $max-columns: 8;

  .selector {
    grid-template-columns: repeat(auto-fit, $tile-width);
    gap: $tile-gap;
    max-width: $max-columns * $tile-width + ($max-columns - 1) * $tile-gap;
    margin-inline: auto;
  }
}
</style>
